import { useEffect, useState } from 'react';
import styles from './ActivitySlotsModal.module.css';
import { supAddTimeSlot, supDeleteActivitySlot, supUpdateActivitySlot } from '../../../../api/supportAdmin';
import { toast } from 'react-toastify';
import ConfirmationModal from '../../../tripStrap/tripStrapFun/cancelBooking';
import InlineTimePicker from '../../../addSlots/InlineTimePicker';
import useSlotManager from '../../../addSlots/useSlotManager';
import moment from 'moment';
import { orgAddTimeSlot, orgDeleteActivitySlot, orgUpdateActivitySlot } from '../../../../api/orgActAPI';
import { orgDeleteGuideSlot, orgUpdateGuideSlot, orgAddTimeSlot as orgAddGuTimeSlot } from '../../../../api/orgGuidAPI';

const emptySlot = {
    startTime: '',
    endTime: '',
    totalSlots: ''
};

export default function ActivitySlotsModal({ activityDate, onClose, onDateUpdated, data, type, isOrg }) {
    const [slots, setSlots] = useState(activityDate?.slots || []);

    const [editingSlotId, setEditingSlotId] = useState(null);

    const [editForm, setEditForm] = useState(emptySlot);

    const [showAddSlot, setShowAddSlot] = useState(false);

    const [addForm, setAddForm] = useState(emptySlot);

    const [saving, setSaving] = useState(false);
    const [deleteSlotData, setDeleteSlotData] = useState(null);
    const {
        customStart,
        customEnd,
        customTotal,
        setCustomStart,
        setCustomEnd,
        setCustomTotal,
        addedSlots,
        handleAddCustomSlot,
        handleStartTimeChange,
        clearCustomSlotForm
    } = useSlotManager({
        data,
        openTime: data?.openTime || '',
        closeTime: data?.closeTime || '',
        activityDate
    });
    useEffect(() => {
        setSlots(activityDate?.slots || []);
    }, [activityDate]);

    if (!activityDate) {
        return null;
    }

    const getDate = () => {
        return new Intl.DateTimeFormat('en-IN', {
            timeZone: 'Asia/Kolkata',
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        }).format(new Date(activityDate.date));
    };

    const startEdit = (slot) => {
        setEditingSlotId(slot._id);

        setEditForm({
            startTime: slot.startTime,
            endTime: slot.endTime,
            totalSlots: slot.totalSlots
        });

        setShowAddSlot(false);
    };

    const cancelEdit = () => {
        setEditingSlotId(null);
        setEditForm(emptySlot);
    };

    const handleEditChange = (field, value) => {
        setEditForm((prev) => ({
            ...prev,
            [field]: value
        }));
    };

    const handleAddChange = (field, value) => {
        setAddForm((prev) => ({
            ...prev,
            [field]: value
        }));
    };

    const saveChanges = async () => {
        try {
            setSaving(true);
            let response;
            if (isOrg) {
                response =
                    type === 'act'
                        ? await orgUpdateActivitySlot(activityDate._id, editingSlotId, {
                              totalSlots: Number(editForm.totalSlots)
                          })
                        : await orgUpdateGuideSlot(activityDate._id, editingSlotId, {
                              totalSlots: Number(editForm.totalSlots)
                          });
            } else {
                response = await supUpdateActivitySlot(activityDate._id, type, editingSlotId, {
                    totalSlots: Number(editForm.totalSlots)
                });
            }
            const updatedDate = response.data.data;
            toast.success(response.data.message);
            setSlots(updatedDate.slots);
            cancelEdit();
            onDateUpdated(updatedDate);
        } catch (error) {
            toast.error(error.response.data.message);
            console.error(error);
        } finally {
            setSaving(false);
        }
    };

    const deleteSlot = async () => {
        if (deleteSlotData.bookedSlots > 0) {
            return;
        }

        try {
            setSaving(true);
            let response;
            if (isOrg) {
                response =
                    type === 'act'
                        ? await orgDeleteActivitySlot(activityDate._id, type, deleteSlotData._id)
                        : await orgDeleteGuideSlot(activityDate._id, type, deleteSlotData._id);
            } else {
                response = await supDeleteActivitySlot(activityDate._id, type, deleteSlotData._id);
            }

            const updatedDate = response.data.data;
            toast.success(response.data.message);
            setDeleteSlotData(null);
            setSlots(updatedDate.slots);

            onDateUpdated(updatedDate);

            if (editingSlotId === deleteSlotData._id) {
                cancelEdit();
            }
        } catch (error) {
            toast.error(error.response.data.message);
            setDeleteSlotData(null);
            console.error(error);
        } finally {
            setSaving(false);
        }
    };

    const addNewSlot = async () => {
        try {
            const isValid = handleAddCustomSlot();

            if (!isValid) return;

            const newSlot = {
                startTime: customStart,
                endTime: customEnd,
                totalSlots: Number(customTotal)
            };

            setSaving(true);
            const dateValue = moment(activityDate.date).format('YYYY-MM-DD');
            let response;
            if (isOrg) {
                response =
                    type === 'act'
                        ? await orgAddTimeSlot(data._id, type, {
                              date: dateValue,
                              slots: [newSlot],
                              bookingEnd: dateValue
                          })
                        : await orgAddGuTimeSlot(data._id, type, {
                              date: dateValue,
                              slots: [newSlot],
                              bookingEnd: dateValue
                          });
            } else {
                response = await supAddTimeSlot(data._id, type, {
                    date: dateValue,
                    slots: [newSlot],
                    bookingEnd: dateValue
                });
            }

            const updatedDate = response.data.data;

            toast.success(response.data.message);

            setSlots(updatedDate.slots);
            onDateUpdated(updatedDate);

            clearCustomSlotForm();
            setShowAddSlot(false);
        } catch (error) {
            const message = error?.response?.data?.message || 'Could not add slot. Please try again.';

            toast.error(message);
            console.error(error);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            className={styles.overlay}
            onMouseDown={(e) => {
                if (e.target === e.currentTarget) {
                    onClose();
                }
            }}
        >
            <div className={styles.modal}>
                {/* HEADER */}
                <div className={styles.header}>
                    <div>
                        <h2>{type === 'act' ? 'Activity' : 'Guide'} Slots</h2>

                        <div className={styles.date}>
                            <span>▣</span>
                            {getDate()}
                            <span>
                                Timings : {data?.openTime} - {data?.closeTime}
                            </span>
                        </div>
                    </div>

                    <div className={styles.headerActions}>
                        <button
                            type='button'
                            className={styles.addSlotButton}
                            onClick={() => {
                                setShowAddSlot(true);
                                cancelEdit();
                            }}
                        >
                            <span>＋</span>
                            Add time Slot
                        </button>

                        <button type='button' className={styles.closeButton} onClick={onClose}>
                            ×
                        </button>
                    </div>
                </div>

                {/* INFO */}
                <div className={styles.info}>
                    <span>ⓘ</span>

                    <span>Edit slot capacity. Changes will be reflected immediately.</span>
                </div>

                {/* SLOT TABLE */}
                <div className={styles.slotTable}>
                    <div className={styles.tableHeader}>
                        <div>Time Slot</div>
                        <div>Capacity</div>
                        <div>Booked</div>
                        <div>Available</div>
                        <div>Action</div>
                    </div>

                    {slots.map((slot) => {
                        const hasBookings = Number(slot.bookedSlots || 0) > 0;

                        return (
                            <div className={styles.slotRow} key={slot._id}>
                                <div className={styles.time}>
                                    <span>◷</span>

                                    {slot.startTime}
                                    {' – '}
                                    {slot.endTime}
                                </div>

                                <div>{slot.totalSlots}</div>

                                <div>{slot.bookedSlots || 0}</div>

                                <div className={styles.available}>{slot.availableSlots || 0}</div>

                                <div className={styles.actions}>
                                    <button type='button' className={styles.editButton} onClick={() => startEdit(slot)}>
                                        ✎ Edit
                                    </button>

                                    {!hasBookings && (
                                        <>
                                            <span className={styles.divider} />

                                            <button
                                                type='button'
                                                className={styles.deleteButton}
                                                onClick={() => setDeleteSlotData(slot)}
                                            >
                                                🗑 Delete
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* EDIT SECTION */}
                {editingSlotId && (
                    <div className={styles.formSection}>
                        <div className={styles.formTitle}>
                            <div>
                                <h3>Edit Slot</h3>

                                <span>
                                    {editForm.startTime}
                                    {' – '}
                                    {editForm.endTime}

                                    {slots.find((slot) => slot._id === editingSlotId)?.bookedSlots > 0 && (
                                        <b> • {slots.find((slot) => slot._id === editingSlotId)?.bookedSlots} booked</b>
                                    )}
                                </span>
                            </div>

                            <button type='button' onClick={cancelEdit} className={styles.collapseButton}>
                                ×
                            </button>
                        </div>

                        <div className={styles.formGrid}>
                            <div>
                                <label>Start Time</label>

                                <input
                                    type='text'
                                    value={editForm.startTime}
                                    onChange={(e) => handleEditChange('startTime', e.target.value)}
                                    disabled
                                />
                            </div>

                            <div>
                                <label>End Time</label>

                                <input
                                    type='text'
                                    value={editForm.endTime}
                                    onChange={(e) => handleEditChange('endTime', e.target.value)}
                                    disabled
                                />
                            </div>

                            <div>
                                <label>Total Slots</label>

                                <input
                                    type='number'
                                    min='1'
                                    value={editForm.totalSlots}
                                    onChange={(e) => handleEditChange('totalSlots', e.target.value)}
                                />
                            </div>
                        </div>

                        {(() => {
                            const slot = slots.find((item) => item._id === editingSlotId);

                            if (!slot) return null;

                            return (
                                <div className={styles.formInfo}>
                                    Booked: <b>{slot.bookedSlots || 0}</b>
                                    <span>•</span>
                                    Available: <b>{slot.availableSlots || 0}</b>
                                    <span>•</span>
                                    Total slots cannot be less than booked slots.
                                </div>
                            );
                        })()}

                        <div className={styles.formActions}>
                            <button
                                type='button'
                                className={styles.cancelButton}
                                onClick={cancelEdit}
                                disabled={saving}
                            >
                                Cancel
                            </button>

                            <button type='button' className={styles.saveButton} onClick={saveChanges} disabled={saving}>
                                {saving ? 'Saving...' : 'Save Changes'}
                            </button>
                        </div>
                    </div>
                )}

                {/* ADD NEW SLOT */}
                {showAddSlot && (
                    <div className={styles.addSection}>
                        <div className={styles.addTitle}>
                            <h3>Add New Slot</h3>

                            <span>NEW</span>
                        </div>

                        <div className={styles.addGrid}>
                            <div>
                                <label>Start Time</label>

                                {/* <input
                                    type='text'
                                    placeholder='Select start time'
                                    value={addForm.startTime}
                                    onChange={(e) => handleAddChange('startTime', e.target.value)}
                                /> */}
                                <InlineTimePicker value={customStart} onChange={handleStartTimeChange} />
                            </div>

                            <div>
                                <label>End Time</label>

                                {/* <input
                                    type='text'
                                    placeholder='Select end time'
                                    value={addForm.endTime}
                                    onChange={(e) => handleAddChange('endTime', e.target.value)}
                                /> */}
                                <InlineTimePicker
                                    value={customEnd}
                                    onChange={setCustomEnd}
                                    minTime={customStart}
                                    disabled={!customStart}
                                />
                            </div>

                            <div>
                                <label>Total Slots</label>

                                <input
                                    type='number'
                                    min='1'
                                    placeholder='Enter total slots'
                                    value={customTotal}
                                    onChange={(e) => {
                                        const value = e.target.value;

                                        setCustomTotal(value === '' ? '' : Number(value));
                                    }}
                                    className={styles.totalSeatsInput}
                                />
                            </div>

                            <div className={styles.addActions}>
                                <button
                                    type='button'
                                    className={styles.cancelButton}
                                    onClick={() => {
                                        setShowAddSlot(false);
                                        clearCustomSlotForm();
                                    }}
                                >
                                    Cancel
                                </button>

                                <button
                                    type='button'
                                    className={styles.saveButton}
                                    onClick={addNewSlot}
                                    disabled={saving}
                                >
                                    {saving ? 'Adding...' : 'Add Slot'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* FOOTER */}
                <div className={styles.footer}>
                    <button type='button' className={styles.footerClose} onClick={onClose}>
                        Close
                    </button>
                </div>
                {deleteSlotData && (
                    <ConfirmationModal
                        setShowModal={setDeleteSlotData}
                        handleConfirm={deleteSlot}
                        message={' Are you sure you want to delete this time slot?'}
                    />
                )}
            </div>
        </div>
    );
}
